import "./BulletinBoard.scss";
import { ReactComponent as IconSend } from "assets/images/icons/send.svg";
import UserDetail from "components/ui/UserDetail/UserDetail";
import BackBar from "components/ui/BackBar/BackBar";
import Button from "components/ui/Button/Button";
import Labels from "components/ui/Labels/Labels";
import ContentContainer from "layout/Content/ContentContainer";
import useEntity from "hooks/useEntity";
import Alert from "components/ui/Alert/Alert";
import ResponsiveImage from "components/ui/ResponsiveImage/ResponsiveImage";
import Loader from "components/ui/Loader/Loader";
import Comments from "features/comments/components/Comments";
import CommentCreateUpdate from "features/comments/components/CommentCreateUpdate";
import { useDialog } from "context/DialogContext";
import { useEffect, useState } from "react";
import MessageCreate from "pages/MessageBox/MessageCreate";
import BulletinOwnerButtons from "features/bulletins/components/BulletinOwnerButtons";
import { useAccount } from "context/AccountContext";
import { useHistory } from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import BulletinActionUnsubscribe from "features/bulletins/components/BulletinActionUnsubscribe";
import HTMLContainer from "components/ui/HTML/HTMLContainer";
import { useGroupAccess } from "features/groups/hooks/useGroupAccess";
import CommentCreateUpdateNotAllowed from "features/comments/components/CommentCreateUpdateNotAllowed";
import ButtonLike from "features/likes/components/ButtonLike/ButtonLike";
import ParticipationStatusBar from "components/ui/ParticipationStatusBar/ParticipationStatusBar";

function BulletinBoardDetail() {
  const query = useQuery();
  const { user } = useAccount();
  const history = useHistory();
  const [ refreshToken, setRefreshToken ] = useState(null);
  const { entity, isLoading } = useEntity(window.location.pathname, 'bulletin', refreshToken);
  const { showDialog } = useDialog();
  const { working, checkAccess } = useGroupAccess();
  const [ likeCount, setLikeCount ] = useState(0);

  useEffect(
    () => {
      if (query.get('action') === 'unsubscribe' && entity?.id) {
        showDialog(<BulletinActionUnsubscribe entity_id={ entity.id }/>);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ entity ]
  );

  const handleClickComment = async () => {
    const groupId = entity.field_group?.id;
    if (groupId) {
      const data = await checkAccess(groupId);
      if (!data?.is_member) {
        showDialog(<CommentCreateUpdateNotAllowed/>);
        return;
      }
    }

    showDialog(<CommentCreateUpdate entityId={ entity.id }
                                    groupId={ groupId }
                                    isOwner={ entity.uid === user.id }
                                    onCompleted={ refresh }/>);
  }

  if (isLoading) {
    return <Loader containerVariant={ 'fixed' }/>;
  }

  if (!entity) {
    return <Alert/>;
  }

  const refresh = () => {
    setRefreshToken(Math.random());
  }

  const backPath = entity?.field_group?.url && entity?.field_group?.url + '?tab=Groepsberichten';
  const isHighlightedItem = Number(entity.field_bulletin_type?.field_is_highlighted?.value) === 1 ||
    Number(entity?.bulletin_parent_type?.field_is_highlighted?.value) === 1;

  const commentButtonText = entity?.field_bulletin_type.field_comment_button_text.value || entity?.bulletin_parent_type.field_comment_button_text.value || `Reageer`;

  return (
    <>
      <BackBar path={ backPath } containerWidth={ 'small' } label={ 'Terug naar overzicht' }/>

      <ContentContainer contentGridType={ 'small' } contentPadding={ true } contentBackground={ true }>
        <div className={ `bulletinboard__header` }>
          <Labels items={ [ isHighlightedItem ? entity.created.month : entity.field_bulletin_type?.name?.value ] } variant={ 'dark' }/>
          <ButtonLike
            entityId={ parseInt(entity.id) }
            entityType={ `bulletin` }
            onReceivedCount={ (count) => setLikeCount(count) }
          />
        </div>

        <div className={ 'wrapper--margin-small' }>
          <h2>{ entity.title.value }</h2>
          <h4>{ entity.created.default }</h4>

          {
            entity.profile?.title &&
              <UserDetail image={ entity.profile?.field_profile_photo?.value }
                          username={ entity.profile?.title?.value }
                          subtext={ entity.profile?.field_profile_city?.value }
                          marginBottom={ true }
                          url={ entity.profile?.url }
              />
          }
          {
            entity.profile?.id &&
            <Button variant={ 'ghost' } className={ 'button--small' }
                    onClick={ () => showDialog(<MessageCreate receiver={ entity.profile }/>) }>
              Verstuur privebericht
            </Button>
          }
        </div>

        <div className={ 'wrapper--margin-small' }>
          <h3>Bericht</h3>

          <div className={ 'wrapper--margin-xsmall' }>
            <HTMLContainer>
              { entity.field_description.display }
            </HTMLContainer>
          </div>

          {
            entity.field_bulletin_image.value &&
            <div className={ 'wrapper--margin-xsmall' }>
              <ResponsiveImage value={ entity.field_bulletin_image.value }/>
            </div>
          }

          {
            user.id === entity.uid &&
            <div className={ 'button-container wrapper--margin-xsmall' }>
              <BulletinOwnerButtons bulletinId={ parseInt(entity.id) }
                                    bulletinTitle={ entity.title.value }
                                    onDeleted={ () => history.push('/prikbord/mijn-prikbord') }
                                    onEdited={ refresh }
                                    categoryId={ entity?.bulletin_parent_type?.id }
              />
            </div>
          }
        </div>

        <ParticipationStatusBar
          commentCount={ Number(entity.field_comments.comment_count || 0) }
          likeCount={ likeCount }
        />

        <div className={ 'wrapper--margin-medium' }>
          <h3>Reacties</h3>

          <div className={ 'wrapper--margin-xsmall' }>
            {
              working
                ?
                <Loader loaderVariant={ `small` }/>
                :
                <Button variant={ 'secondary' }
                        onClick={ handleClickComment }>
                  <IconSend/>
                  { commentButtonText }
                </Button>
            }
          </div>

          <Comments entity_id={ entity.id } bundle={ 'bulletin' } refreshToken={ refreshToken } onRefresh={ refresh }/>
        </div>
      </ContentContainer>
    </>
  );
}

export default BulletinBoardDetail;
